// The changes people made, as the System log reads them (`/api/routing/changes`): the hook the
// System log page and a line's history share, and a line's history as a list. Only `ChangeView`s:
// where they come from (`change-entry` today, `audit_log` after P13) is the server's.

import { useQuery } from "@tanstack/react-query";
import { ROUTING_API, changeText, clockText, formatDay, localToday } from "@app/shared";
import type { ChangeView, ChangesPayload } from "@app/shared";
import { apiGet, buildQuery } from "@axny/platform-ui";
import { SAFETY_REFETCH_MS } from "./data";

export const changesKey = (q: { line?: string; from?: string | null; to?: string | null }) =>
  ["routing", "changes", q.line ?? "", q.from ?? "", q.to ?? ""] as const;

/** A line's changes, or those of the days `from`–`to`; refetched every minute like the pages. */
export function useChanges(q: { line?: string; from?: string | null; to?: string | null }, enabled = true) {
  return useQuery({
    queryKey: changesKey(q),
    queryFn: ({ signal }) =>
      apiGet<ChangesPayload>(
        `${ROUTING_API}/changes${buildQuery({ line: q.line, from: q.from ?? undefined, to: q.to ?? undefined })}`,
        signal,
      ),
    enabled,
    refetchInterval: SAFETY_REFETCH_MS,
    // Read again when someone is back (a tab shown, idle ended): the platform's default is false.
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
}

/** "10/21, 4:12 PM" in the reader's zone; an older entry has its day only ("10/21/26"). */
export const changedAt = (c: Pick<ChangeView, "at" | "timeKnown">) =>
  !c.at
    ? ""
    : c.timeKnown
      ? `${localToday(new Date(c.at)).slice(5).replace("-", "/")}, ${clockText(new Date(c.at))}`
      : formatDay(c.at.slice(0, 10));

/** A line's history (spec §3.9, §3.14): who changed what, newest first. */
export function LineHistory({ lineId }: { lineId: string }) {
  const changes = useChanges({ line: lineId }, !!lineId);
  return (
    <section data-testid="line-history">
      <h3 className="text-muted-foreground mb-1 text-xs font-semibold tracking-wider uppercase">History</h3>
      {changes.isPending ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : changes.isError ? (
        <p className="text-destructive">The System log could not be read: {changes.error.message}</p>
      ) : !changes.data.changes.length ? (
        <p className="text-muted-foreground">No change by people since the System log started.</p>
      ) : (
        <ol className="space-y-1.5">
          {changes.data.changes.map((c) => (
            <li key={c.id} className="border-l-2 pl-2">
              <p>{changeText(c)}</p>
              <p className="text-muted-foreground text-xs">
                {changedAt(c)} · {c.personName}
                {c.edited || c.otherWriter ? " · ⚠ this entry was changed after it was written" : ""}
              </p>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
